import { groupCountryNames } from 'components/country-select/countries'
import { formatAsnWithName, parseAsn } from './network-values'
import { triggerOrdinal } from './rule-options'
import { CRITERIA_DEFINITIONS } from './criteria'
import { RuleEntities } from './rule-entities'
import { RuleCriterion, RuleDraft, RuleScope } from './types'

/** Countries to spell out in a sentence before the sentence gives up. */
const MAX_SPELLED_COUNTRIES = 4

/** The country criterion in words: a name, a continent, a list, then a count. */
const describeCountries = (names: string[]): string => {
	const groups = groupCountryNames(names)
	if (groups.length > MAX_SPELLED_COUNTRIES)
		return `one of ${names.length} countries`
	return groups
		.map((group) =>
			group.whole ? `any country in ${group.label}` : group.label
		)
		.join(' or ')
}

// Lowercase only a leading capitalized word, leaving acronyms alone:
// "Detected IP address type" -> "detected IP address type", "IP range" stays.
const decap = (text: string): string =>
	/^[A-Z][a-z]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text

/** One criterion split into the part that names it and the part that states what it
 *  has to be; both the drawer and the row summary read this. Returns null for a
 *  criterion with no value yet, so no dangling clause is printed. */
export const criterionParts = (
	selected: RuleCriterion
): { label: string; statement: string } | null => {
	const definition = CRITERIA_DEFINITIONS.find((d) => d.id === selected.id)
	if (!definition) return null
	if (definition.kind === 'boolean')
		return { label: definition.summaryLabel ?? definition.label, statement: '' }

	const kept = selected.values.filter(Boolean)
	const values =
		definition.kind === 'country'
			? describeCountries(kept)
			: definition.kind === 'network'
			? kept
					.map((value) => {
						const number = selected.id === 'asn' ? parseAsn(value) : null
						return number === null
							? value
							: formatAsnWithName(number, selected.names?.[String(number)])
					})
					.join(', ')
			: kept.join(' or ')

	if (definition.statements) {
		const negated = selected.operator === 'is not'
		const statement = negated
			? definition.statements.negative
			: definition.statements.positive
		// The label only refines the positive case, the way production's
		// conversion modifier does.
		return {
			label: definition.summaryLabel ?? definition.label,
			statement:
				values && !negated && definition.valuePrefix
					? `${statement} ${definition.valuePrefix} ${values}`
					: statement
		}
	}

	if (!values) return null
	const unit = definition.unit ? ` ${definition.unit}` : ''
	return {
		label: definition.summaryLabel ?? definition.label,
		statement: `${selected.operator || 'is'} ${values}${unit}`
	}
}

const describeCriterion = (criterionId: string, draft: RuleDraft): string => {
	const selected = draft.criteria.find((c) => c.id === criterionId)
	if (!selected) return ''
	const parts = criterionParts(selected)
	if (!parts) return ''
	return `${decap(parts.label)} ${parts.statement}`.trim()
}

/** "this website", or the targets a rule has been narrowed to. */
export const describeScope = (
	entities: RuleEntities,
	scope: RuleScope
): string => {
	if (scope.mode === 'website') return 'this website'
	// Widened to the shared shape so the lookup works across both directories.
	const directory: { id: string; name: string }[] =
		scope.mode === 'accounts' ? entities.adAccounts : entities.campaigns
	const names = scope.values
		.map((id) => directory.find((entry) => entry.id === id)?.name)
		.filter(Boolean) as string[]
	const noun = scope.mode === 'accounts' ? 'ad account' : 'campaign'
	if (names.length === 0) return `selected ${noun}s`
	if (names.length === 1) return names[0]
	return `${names.length} ${noun}s`
}

/** Live verbal summary of the rule, mirroring the production RuleSummary. */
export const describeRule = (
	entities: RuleEntities,
	draft: RuleDraft
): string => {
	const { trigger, criteria, threshold, actions } = draft

	const periodUnit =
		trigger.periodValue === 1
			? trigger.periodUnit.replace(/s$/, '')
			: trigger.periodUnit
	const parts: string[] = [
		`Trigger on ${
			trigger.maxClicks === '' ? '…' : triggerOrdinal(trigger.maxClicks)
		} click ` +
			`from the same ${decap(trigger.focus)} ` +
			`within ${trigger.periodValue} ${periodUnit} on ` +
			`${describeScope(entities, draft.scope)}`
	]

	const described = criteria
		.map((c) => describeCriterion(c.id, draft))
		.filter(Boolean)
	if (described.length > 0) {
		const share =
			threshold.mode === 'percentage'
				? `${threshold.percent}% of clicks match`
				: 'all clicks match'
		parts.push(`and ${share}: ${described.join('; ')}`)
	}

	const outcomes: string[] = [
		`mark the click ${actions.clickStatus.toLowerCase()}`
	]
	if (actions.addLabels && actions.labels.trim()) {
		outcomes.push(`label it "${actions.labels.trim()}"`)
	}
	if (actions.blacklist) {
		const unit =
			actions.blacklistValue === 1
				? actions.blacklistUnit.replace(/s$/, '')
				: actions.blacklistUnit
		outcomes.push(`blacklist the source for ${actions.blacklistValue} ${unit}`)
	}
	if (actions.tempIpExclusion && !actions.blacklist) {
		outcomes.push('temporarily exclude the IP on the targeted campaign')
	}
	if (actions.audienceExclusion && !actions.blacklist) {
		outcomes.push('add the visitor to the excluded audience')
	}

	const outcomeList =
		outcomes.length > 1
			? `${outcomes.slice(0, -1).join(', ')} and ${
					outcomes[outcomes.length - 1]
			  }`
			: outcomes[0]

	return `${parts.join(' ')}. ClickGuard will ${outcomeList}.`
}
